import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { useState, useEffect } from "react";
import {
  Home, Users, Calculator, CheckSquare, Calendar, CalendarCheck, Store, Utensils,
  StickyNote, FileText, CreditCard, Headphones, LifeBuoy, Folder, Globe,
  Heart, TrendingUp, Settings, Mail, ClipboardList, HardDrive, Wallet, Activity, MessageSquare, MessageCircle, Package, Brain, Star, Gift, CalendarDays, Palette, Building2, Crown, Car, ShoppingBag, Smartphone, DollarSign, Bot, Phone, PhoneCall, Shield, Database, BarChart3, Wrench, MessageSquareText, Cog, HardDriveIcon, ShoppingCart, Mic, Menu, ChevronLeft, Monitor, Megaphone, Video, Camera, User, Eye, Film, Workflow, Contact, Banknote, ListTodo, HelpCircle, Gamepad2, GraduationCap, HandHeart, Radio, Bus, MonitorPlay, Clapperboard
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import laMirageLogo from "@assets/download.png";

import { useQuery } from "@tanstack/react-query";

// Sidebar collapse context
interface SidebarContextType {
  isCollapsed: boolean;
  toggleSidebar: () => void;
}

let sidebarContext: SidebarContextType = {
  isCollapsed: false,
  toggleSidebar: () => {}
};

export const useSidebar = () => sidebarContext;

/** Admin nav item. `badgeQueryKey` (optional): a GET URL returning `{ pending: number }`, shown as a gold count badge. */
interface AdminNavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  badgeQueryKey?: string;
  disabled?: boolean;
}
interface AdminNavTab { id: string; label: string; icon: LucideIcon; items: AdminNavItem[] }

/** Count badge for a nav item. Renders NOTHING while loading, on error (e.g. 401/403), or at zero. */
function NavBadge({ queryKey }: { queryKey: string }) {
  const { data, isError } = useQuery<{ pending: number }>({
    queryKey: [queryKey],
    refetchInterval: (q) => (q.state.status === "error" ? false : 60_000), // stop polling after a 401/403
    staleTime: 30_000,
  });
  const n = Number(data?.pending);
  if (isError || !Number.isFinite(n) || n <= 0) return null;
  return <span className="ml-auto rounded-full bg-[#B8860B] px-2 text-xs font-semibold text-black relative z-10">{n}</span>;
}

const getAdminTabs = (): AdminNavTab[] => [
  {
    id: "overview",
    label: "Overview",
    icon: BarChart3,
    items: [
      { href: "/", label: "Admin Dashboard", icon: Home },
      { href: "/admin/analytics", label: "Analytics & Reports", icon: TrendingUp },
      { href: "/events-calendar", label: "Events Calendar", icon: Calendar },
      // ONE entry, tabs inside (?tab=bookings|abandoned|holidays). ⚠️ Do NOT add sub-items
      // with '#' hrefs — wouter treats /admin/bookings#x as a PATH, fails to match, and
      // falls through to the 404. Same lesson recorded on the Care entry below.
      { href: "/admin/bookings", label: "Bookings", icon: CalendarCheck },
    ],
  },
  {
    id: "events",
    label: "Events",
    icon: Heart,
    items: [
      { href: "/admin/clients", label: "Clients", icon: Users },
      { href: "/admin/weddings", label: "Event Management", icon: Heart },
      { href: "/admin/stage-selections", label: "Event Day Forms", icon: Crown },
      { href: "/admin/event-team-contacts", label: "Event Team Contacts", icon: Users },
      { href: "/admin/reschedule-requests", label: "Reschedule Requests", icon: CalendarDays },
      { href: "/admin/reviews", label: "Review Management", icon: Star },
      { href: "/admin/loyalty", label: "Loyalty Program", icon: Gift },
      { href: "/admin/live-photos", label: "Live Photos", icon: Camera },
      { href: "/visualizer", label: "Vision Studio", icon: Eye },
      { href: "/admin/journey-studio", label: "Journey Studio", icon: Clapperboard },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    icon: DollarSign,
    items: [
      { href: "/admin/finance-hub", label: "Finance Hub", icon: Wallet },
      { href: "/admin/payments", label: "Event Day Payments", icon: Wallet },
      { href: "/admin/internal-maintenance", label: "Maintenance", icon: Wrench },
      { href: "/admin/local-purchase", label: "Local Purchase", icon: ShoppingCart },
      { href: "/admin/catering-advances", label: "Catering Advances", icon: CreditCard },
      /**
       * 🔴 A SECOND ENTRY POINT, on purpose. The same page is also linked under Vendors as
       * "Add-on Marketplace" — a name and a place that suggest a CATALOGUE, which is what that
       * tab is. But the page also now holds a client's own add-on lines and the invoice raised
       * over them, and nobody looking for a client's bill goes to Vendors → Marketplace. The
       * owner looked for it and did not find it (2026-09-13).
       *
       * Deep-links straight to the tab so the landing page is the one being asked for.
       */
      { href: "/admin/addons?tab=client-lines", label: "Client Add-ons & Invoices", icon: ShoppingBag },
      { href: "/admin/salary", label: "Salary Management", icon: DollarSign },
      // Position mirrors the Finance Hub tab order (after Salary) — owner decision.
      // The active highlight (a pathname-only compare below) will not light for a
      // `?tab=` item — cosmetic, and deliberately not worked around.
      { href: "/admin/finance-hub?tab=invoices", label: "Invoices", icon: FileText },
      { href: "/admin/expenses", label: "Expenses", icon: Wallet },
      { href: "/admin/accounting", label: "Accounting Dashboard", icon: Calculator },
      { href: "/admin/gst-report", label: "GST Report", icon: FileText },
      { href: "/admin/expense-report", label: "Expense Report", icon: ClipboardList },
      { href: "/zoho-settings", label: "Zoho Books Settings", icon: Settings },
      // Which rail money leaves on. Admin-only (the API is admin-gated) and
      // deliberately last in Finance — it is an outage tool, not a daily task.
      { href: "/admin/payout-providers", label: "Payout Provider", icon: Banknote },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    icon: Wrench,
    items: [
      { href: "/admin/operations-contacts", label: "Contacts", icon: Contact },
      { href: "/admin/my-tasks", label: "My Tasks", icon: ListTodo },
      { href: "/admin/employees", label: "Employee Calendar", icon: Users },
      { href: "/admin/mc-management", label: "MC Management", icon: Crown },
      { href: "/admin/mc-schedules", label: "MC Schedules", icon: Calendar },
      { href: "/admin/mc-calendar", label: "MC Calendar", icon: CalendarDays },
      { href: "/admin/dance-calendar", label: "Dance Calendar", icon: CalendarDays },
      { href: "/admin/transport", label: "Transport", icon: Bus },
      { href: "/admin/valet-management", label: "Valet Management", icon: Car },
      { href: "/admin/vr-management", label: "VR Experience", icon: Gamepad2 },
      { href: "/admin/domain-requests", label: "Domain Requests", icon: Globe },
      { href: "/admin/inventory", label: "Inventory", icon: Package },
      { href: "/admin/venue-management", label: "Venue Hub", icon: Building2 },
      { href: "/admin/amazon", label: "Amazon Business", icon: Store },
      { href: "/admin/salary-approval", label: "Payment Approval", icon: CheckSquare },
      { href: "/admin/salary-reports", label: "Salary Reports", icon: BarChart3 },
      { href: "/admin/catering", label: "Catering Management", icon: Utensils },
      { href: "/admin/stage-designs", label: "Stage Design Management", icon: Palette },
      { href: "/admin/signage", label: "Signage", icon: MonitorPlay },
      { href: "/admin/signage/live", label: "Screens · Live", icon: Radio },
    ],
  },
  {
    id: "vendors",
    label: "Vendors",
    icon: Store,
    items: [
      { href: "/admin/vendors", label: "Vendor Management", icon: Store },
      { href: "/admin/vendor-management", label: "Vendor Approvals", icon: ShoppingBag },
      { href: "/admin/addons", label: "Add-on Marketplace", icon: ShoppingBag },
      { href: "/admin/artists", label: "Artists Portfolio", icon: Mic },
    ],
  },
  {
    id: "communication",
    label: "Communication",
    icon: MessageSquareText,
    items: [
      // Support Center took over Comm-Hub's prime slot (CommHub retired quietly;
      // /comm-hub route still works, it just lost the placement).
      { href: "/admin/support", label: "Support Center", icon: LifeBuoy },
      { href: "/meetings", label: "Meetings", icon: Video },
      { href: "/admin/live-chat-enterprise", label: "WhatsApp Support", icon: MessageCircle },
      { href: "/admin/announcements", label: "System Announcements", icon: Megaphone },
      { href: "/admin/notifications", label: "WhatsApp Notifications", icon: MessageSquare },
      { href: "/admin/whatsapp-templates", label: "WhatsApp Templates", icon: MessageSquare },
      { href: "/admin/email-management", label: "Email Management", icon: Mail },
      { href: "/zoho-desk-settings", label: "Zoho Desk Settings", icon: Headphones },
    ],
  },
  {
    id: "ai",
    label: "AI",
    icon: Bot,
    items: [
      { href: "/admin/ai-dashboard", label: "AI Admin Dashboard", icon: Bot },
      { href: "/admin/ai-knowledge", label: "AI Knowledge Base", icon: Brain },
      { href: "/admin/ai-assistant", label: "LAMirage AI Management", icon: MessageSquare },
      // Agent proposals are approved ONLY here (owner rule 2026-10-06); badge = pending count.
      { href: "/admin/agents", label: "Agents", icon: Bot, badgeQueryKey: "/api/admin/agents/pending-count" },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: Cog,
    items: [
      { href: "/admin/users", label: "User Management", icon: Users },
      { href: "/admin/settings", label: "System Settings", icon: Settings },
      { href: "/admin/mfa", label: "Multi-Factor Authentication", icon: Shield },
      { href: "/admin/venue-config", label: "Venue & Operations", icon: Building2 },
      { href: "/admin/welcome-config", label: "Welcome Messages", icon: Heart },
      { href: "/admin/voice-agent", label: "Voice AI Agent", icon: Phone },
      { href: "/admin/voice-call-history", label: "Voice Call History", icon: PhoneCall },
      { href: "/admin/security", label: "Security", icon: Shield },
      // Transport hub entry lives in the Operations tab (/admin/transport).
      { href: "/admin/mobile-navigation", label: "Mobile Navigation", icon: Smartphone },
      { href: "/admin/mobile-app", label: "Mobile App Management", icon: Smartphone },
      { href: "/admin/otp-testing", label: "Global OTP Testing", icon: Globe },
    ],
  },
  {
    id: "data",
    label: "Data",
    icon: HardDriveIcon,
    items: [
      { href: "/admin/storage", label: "Storage Management", icon: HardDrive },
      { href: "/admin/backups", label: "Backup Management", icon: Database },
      { href: "/admin/performance", label: "Performance Monitoring", icon: Monitor },
      { href: "/admin/zoho-activities", label: "CRM Activities", icon: Activity },
    ],
  },
  {
    // Single entry — the 5 tabs inside /admin/care (Overview / Scholarship /
    // Medical / Public Preview / Settings) handle navigation. Sub-items with
    // hash hrefs broke because wouter Link treats /admin/care#x as a path,
    // doesn't match the route, and falls through to 404.
    id: "care",
    label: "Care",
    icon: HandHeart,
    items: [
      { href: "/admin/care", label: "Care portal", icon: HandHeart },
    ],
  },
];

// Roles whose sidebar is the CLIENT menu. This function's final `return` has no default
// branch, so an unrecognised role used to be handed the client navigation (Family Hub,
// invoices, wedding website, add-ons). Same defect class as the client-portal fallthrough
// in client/src/App.tsx. A role not listed here gets an EMPTY menu, never the client one.
// ⛔ Do not add 'partner' — the partner portal ships its own navigation.
const CLIENT_NAV_ROLES = ['client', 'collaborator', 'planner'];
// Roles that have their own branch further down this function.
const OWN_NAV_ROLES = ['admin', 'event_manager', 'operations_manager', 'vendor', 'employee', 'accountant'];

const getNavigationItems = (userRole: string, weddingId?: number, signageShow = false) => {
  if (!CLIENT_NAV_ROLES.includes(userRole) && !OWN_NAV_ROLES.includes(userRole)) {
    return [];
  }

  if (userRole === 'admin') {
    // Return empty for admin as we'll use tabs
    return [];
  }

  if (userRole === 'event_manager') {
    return [
      {
        title: "Event Management",
        items: [
          { href: "/", label: "Dashboard", icon: Home },
          { href: "/admin/support", label: "Support Center", icon: LifeBuoy },
          { href: "/admin/clients", label: "Clients", icon: Users },
          // Owner 2026-09-12: event managers get Bookings — the Bookings, Waiting list and
          // Abandoned tabs. ONE entry, tabs inside; do NOT add sub-items with '#' hrefs
          // (wouter reads /admin/bookings#x as a PATH and falls through to the 404).
          // The Holidays / Traffic / Settings tabs are hidden for this role in bookings.tsx,
          // and their APIs are admin-only regardless — the UI is convenience, not the boundary.
          { href: "/admin/bookings", label: "Bookings", icon: CalendarCheck },
          { href: "/admin/stage-selections", label: "Event Day Forms", icon: Crown },
          { href: "/events-calendar", label: "Events Calendar", icon: Calendar },
          { href: "/admin/mc-calendar", label: "MC Calendar", icon: CalendarDays },
          { href: "/admin/events-hub", label: "Events Hub", icon: Heart },
          { href: "/admin/live-streaming", label: "Live Studio", icon: Monitor },
          { href: "/admin/live-photos", label: "Live Photos", icon: Camera },
          { href: "/admin/signage", label: "Signage", icon: MonitorPlay },
          { href: "/admin/signage/live", label: "Screens · Live", icon: Radio },
          { href: "/admin/valet-management", label: "Valet Management", icon: Car },
          { href: "/admin/transport", label: "Transport", icon: Bus },
          // Removed from event-manager side menu (per owner): Finance Hub, Venue Hub, Voice Call History.
          { href: "/admin/security", label: "Security", icon: Shield },
          { href: "/admin/my-tasks", label: "My Tasks", icon: ListTodo },
          { href: "/admin/care", label: "Care portal", icon: HandHeart },
          { href: "/admin/help", label: "Help", icon: HelpCircle },
        ],
      },
    ];
  }

  if (userRole === 'operations_manager') {
    return [
      {
        title: "Operations Management",
        items: [
          { href: "/", label: "Dashboard", icon: Home },
          { href: "/admin/clients", label: "Clients", icon: Users },
          { href: "/events-calendar", label: "Events Calendar", icon: Calendar },
          { href: "/admin/finance-hub", label: "Finance Hub", icon: Banknote },
          { href: "/admin/venue-management", label: "Venue Hub", icon: Building2 },
          { href: "/admin/transport", label: "Transport", icon: Bus },
          { href: "/admin/salary-reports", label: "Salary Reports", icon: BarChart3 },
          { href: "/admin/support", label: "Support Center", icon: LifeBuoy },
          { href: "/admin/email-management", label: "Email Management", icon: Mail },
          { href: "/admin/ai-knowledge", label: "AI Knowledge Base", icon: Brain },
          { href: "/admin/my-tasks", label: "My Tasks", icon: ListTodo },
        ],
      },
    ];
  }

  if (userRole === 'vendor') {
    return [
      {
        title: "Vendor Management",
        items: [
          { href: "/vendor/dashboard", label: "Vendor Dashboard", icon: Home },
          { href: "/vendor/profile", label: "Profile Settings", icon: Settings },
          { href: "/vendor/products", label: "Manage Products", icon: Package },
          { href: "/vendor/messages", label: "Client Messages", icon: Mail },
          { href: "/vendor/bookings", label: "Bookings", icon: Calendar },
          { href: "/vendor/reviews", label: "Customer Reviews", icon: Star },
        ],
      },
    ];
  }

  if (userRole === 'employee') {
    return [
      {
        title: "",
        items: [
          { href: "/", label: "Dashboard", icon: Home },
          { href: "/employee/schedule", label: "My Calendar", icon: Calendar },
        ],
      },
    ];
  }

  // Accountant navigation - read-only access to accounting reports
  if (userRole === 'accountant') {
    return [
      {
        title: "Accounting Reports",
        items: [
          { href: "/admin/accounting", label: "Accounting Dashboard", icon: Calculator },
          { href: "/admin/gst-report", label: "GST Invoice Report", icon: FileText },
          { href: "/admin/expense-report", label: "Expense Report", icon: ClipboardList },
        ],
      },
    ];
  }

  // Default client navigation
  return [
    {
      title: "",
      items: [
        { href: "/", label: "Dashboard", icon: Home },
        // Help & Support removed from sidebar 2026-07-12 (owner): the header
        // Support button is the single entry point.
        { href: "/bookings", label: "Bookings", icon: Calendar },
        { href: "/invoices", label: "Invoices", icon: CreditCard },
        ...(weddingId ? [
          { href: `/event-form`, label: "Event Form (New)", icon: Palette },
          { href: `/story-weaver`, label: "Story Weaver", icon: Film },
          { href: `/mc-scheduling`, label: "MC Scheduling", icon: Mic },
          { href: `/live-stream`, label: "Live Stream", icon: Radio },
          { href: `/live-photos`, label: "Live Photos", icon: Camera },
          // "Your screen show" — only when the server flag is on for this couple (Phase C C8; hidden 2026-09-24).
          ...(signageShow ? [{ href: `/event-screens`, label: "Your screen show", icon: MonitorPlay }] : []),
        ] : []),
        { href: "/client/addons", label: "Add-ons & Services", icon: ShoppingBag },
        { href: "/loyalty-program", label: "Loyalty Program", icon: Gift },
        { href: "/family-hub", label: "Family Hub", icon: Users },
        { href: "/client-transport", label: "Transport", icon: Car },
      ],
    },
    {
      title: "Wedding Planning",
      items: [
        { href: "/website-v2", label: "Wedding Website", icon: Globe },
        { href: "/budget", label: "Budget Calculator", icon: Calculator },
        { href: "/invitations", label: "Wedding Invitations", icon: Mail },
        // Vendor Directory hidden from the client menu 2026-09-25 (owner): LA Mirage
        // sources its own vendors, so the client-facing directory only invites confusion.
        // { href: "/vendors", label: "Vendor Directory", icon: Store },
        // My Wedding Vendors and Handy Notes hidden (owner, 2026-09-24: LA Mirage now runs catering
        // and photography itself; keep the menu clean).
        // All of these ROUTES still resolve, so existing direct links keep working.
        { href: "/checklist", label: "Wedding Checklist", icon: CheckSquare },
        { href: "/ai-timeline", label: "Timeline & Tasks", icon: Calendar },
      ],
    },

  ];
};

export default function Sidebar() {
  const [location] = useLocation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Update the global context
  sidebarContext.isCollapsed = isCollapsed;
  sidebarContext.toggleSidebar = () => setIsCollapsed(!isCollapsed);

  // Get user's weddings to extract wedding ID for countdown generator
  const { data: weddings } = useQuery({
    queryKey: ['/api/weddings'],
    enabled: user?.role === 'client',
  });

  const primaryWeddingId = (weddings as any)?.[0]?.id;
  const { data: signageStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ['/api/signage/client/status'],
    enabled: user?.role === 'client',
    staleTime: 5 * 60_000,
  });
  const navigationItems = getNavigationItems(user?.role || 'client', primaryWeddingId, !!signageStatus?.enabled);

  // Admin tab-based navigation — DO NOT MODIFY THIS BLOCK
  if (user?.role === 'admin') {
    const adminTabs = getAdminTabs();
    const currentTab = adminTabs.find(tab => tab.id === activeTab);

    return (
      <aside className="w-64 bg-gradient-to-b from-slate-900 via-gray-900 to-black shadow-2xl border-r border-amber-500/20 hidden lg:block relative overflow-hidden">
        {/* Luxury Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/10 via-transparent to-yellow-900/5"></div>
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#B8860B]/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-amber-500/5 to-transparent rounded-full blur-2xl"></div>

        <div className="p-4 space-y-3 relative z-10">

          {/* Luxury Tab Navigation */}
          <div className="grid grid-cols-3 gap-2 mb-4 p-2 bg-gradient-to-r from-black/30 to-gray-900/30 rounded-xl border border-[#B8860B]/10 backdrop-blur-sm">
            {adminTabs.map((tab) => {
              const TabIcon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "flex flex-col items-center p-3 rounded-xl text-xs font-medium transition-all duration-300 transform",
                    isActive
                      ? "bg-gradient-to-r from-[#B8860B]/30 to-amber-500/20 text-[#B8860B] border border-[#B8860B]/40 shadow-lg scale-105 backdrop-blur-sm"
                      : "text-white/70 hover:bg-gradient-to-r hover:from-[#B8860B]/10 hover:to-amber-500/5 hover:text-amber-300 hover:scale-102"
                  )}
                >
                  <TabIcon className={cn("w-5 h-5 mb-2", isActive ? "text-[#B8860B]" : "")} />
                  <span className="truncate w-full text-center font-semibold">{tab.label}</span>
                  {isActive && (
                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[#B8860B] rounded-full shadow-lg"></div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Luxury Active Tab Content */}
          <nav className="space-y-1">
            {currentTab?.items.map((item) => {
              const Icon = item.icon;
              const isActive = location === item.href;

              if (item.disabled) {
                return (
                  <div
                    key={item.href}
                    className="flex items-center space-x-3 px-4 py-2 rounded-xl text-sm font-medium cursor-not-allowed opacity-50 bg-gradient-to-r from-gray-800/30 to-gray-900/20 border border-gray-700/30"
                  >
                    <Icon className="w-5 h-5 text-white/40" />
                    <span className="text-white/40">{item.label}</span>
                    <span className="ml-auto text-xs text-white/30 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 px-3 py-1 rounded-full border border-amber-500/30">
                      Coming Soon
                    </span>
                  </div>
                );
              }

              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "flex items-center space-x-3 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 group transform relative overflow-hidden",
                    isActive
                      ? "bg-gradient-to-r from-[#B8860B]/20 to-amber-500/10 text-[#B8860B] shadow-lg backdrop-blur-sm border border-[#B8860B]/40 scale-105"
                      : "text-white/80 hover:bg-gradient-to-r hover:from-[#B8860B]/10 hover:to-amber-500/5 hover:text-amber-300 hover:shadow-md hover:scale-102 border border-transparent hover:border-[#B8860B]/20"
                  )}
                >
                  {isActive && (
                    <div className="absolute inset-0 bg-gradient-to-r from-[#B8860B]/5 to-amber-500/5 rounded-xl"></div>
                  )}
                  <Icon className={cn(
                    "w-5 h-5 transition-all duration-300 relative z-10",
                    isActive ? "text-[#B8860B]" : "text-white/70 group-hover:text-amber-300"
                  )} />
                  <span className="relative z-10 font-semibold">{item.label}</span>
                  {item.badgeQueryKey && <NavBadge queryKey={item.badgeQueryKey} />}
                  {isActive && (
                    <div className="ml-auto w-2 h-2 bg-gradient-to-r from-[#B8860B] to-amber-500 rounded-full shadow-lg relative z-10"></div>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>
    );
  }

  // Non-admin navigation — responsive: hidden on mobile, icon-only on tablet, full on desktop
  // On md (tablet) the sidebar is always icon-only regardless of isCollapsed toggle.
  // On lg+ (desktop) the isCollapsed toggle controls icon-only vs full width.
  const desktopCollapsed = isCollapsed;

  return (
    <aside className={cn(
      "hidden md:block bg-gradient-to-b from-slate-900 via-gray-900 to-black shadow-2xl border-r border-amber-500/20 relative overflow-hidden transition-all duration-300 ease-in-out",
      // Tablet: always icon-only (w-16). Desktop: respect isCollapsed toggle.
      desktopCollapsed ? "md:w-16" : "md:w-16 lg:w-64"
    )}>

      {/* Navigation content */}
      <div className={cn(
        "pb-6 relative z-10 transition-all duration-300",
        desktopCollapsed ? "px-2" : "md:px-2 lg:px-6"
      )}>

        {/* Collapse Toggle Button — only visible on lg+ */}
        <div className="hidden lg:flex justify-end p-2 -mx-2">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={desktopCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="p-2 rounded-lg border border-[#B8860B]/20 text-amber-300 hover:text-[#B8860B] bg-[#B8860B]/5 hover:bg-[#B8860B]/10 transition-colors duration-150"
          >
            {desktopCollapsed ? <Menu className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="space-y-3 mt-2">
          {navigationItems.map((section, index) => {
            // Determine whether labels/section-headers are visible:
            // hidden when tablet (always icon-only) or desktop-collapsed
            const showLabels = !desktopCollapsed;

            return (
              <div key={section.title} className="animate-slide-up">
                {/* Section title — only shown on lg when not collapsed and not the first section */}
                {index > 0 && section.title && (
                  <div className={cn(
                    "uppercase text-xs tracking-wider text-[#B8860B]/50 px-2 py-1 mb-2",
                    // On tablet always hidden; on lg show only when not collapsed
                    desktopCollapsed ? "hidden" : "hidden lg:block"
                  )}>
                    {section.title}
                  </div>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location === item.href;

                    // Handle external links
                    if (item.external) {
                      return (
                        <a
                          key={item.href}
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn(
                            "flex items-center rounded-lg text-sm font-medium transition-colors duration-150 group",
                            // Tablet: always icon-only. Desktop: respect collapse state.
                            desktopCollapsed
                              ? "justify-center p-2"
                              : "md:justify-center md:p-2 lg:justify-start lg:space-x-3 lg:px-4 lg:py-2",
                            "text-white/70 hover:bg-white/5 border-l-[3px] border-transparent"
                          )}
                          title={item.label}
                        >
                          <Icon className="w-5 h-5 shrink-0 text-white/60 group-hover:text-white/90 transition-colors duration-150" />
                          <span className={cn(
                            "font-medium",
                            desktopCollapsed ? "hidden" : "hidden lg:inline"
                          )}>
                            {item.label}
                          </span>
                        </a>
                      );
                    }

                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        className={cn(
                          "flex items-center rounded-lg text-sm font-medium transition-colors duration-150 group",
                          // Tablet: always icon-only. Desktop: respect collapse state.
                          desktopCollapsed
                            ? "justify-center p-2"
                            : "md:justify-center md:p-2 lg:justify-start lg:space-x-3 lg:px-4 lg:py-2",
                          isActive
                            ? "border-l-[3px] border-[#B8860B] bg-[#B8860B]/10 text-[#B8860B]"
                            : "border-l-[3px] border-transparent text-white/70 hover:bg-white/5 hover:text-white/90"
                        )}
                        title={item.label}
                      >
                        <Icon className={cn(
                          "w-5 h-5 shrink-0 transition-colors duration-150",
                          isActive ? "text-[#B8860B]" : "text-white/60 group-hover:text-white/90"
                        )} />
                        <span className={cn(
                          "font-medium",
                          desktopCollapsed ? "hidden" : "hidden lg:inline"
                        )}>
                          {item.label}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}
